import Phaser from 'phaser';

/**
 * Shared selection / focus ring for CTA pill buttons.
 *
 * The cyan PLAY pill is an SVG stretched to each button's width, so its
 * end-caps render as ellipses (wider than tall), not perfect circles. A ring
 * drawn with `strokeRoundedRect` (a single radius) is a true circular pill and
 * bows away from the button around the caps. To stay parallel we offset the
 * button's ACTUAL cap shape: independent x/y corner radii, each grown by `gap`.
 *
 * Rule: outer radius = inner (button) radius + gap, on BOTH axes.
 */
export interface HaloTarget {
  /** center of the wrapped element, in scene coords */
  cx: number;
  cy: number;
  /** wrapped element size */
  w: number;
  h: number;
  /** wrapped element corner radii (rx may differ from ry for a stretched pill) */
  rx: number;
  ry: number;
}

export interface HaloStyle {
  /** even gap between the element edge and the outer ring */
  gap?: number;
  /** how far the inner hairline sits inside the outer ring */
  inset?: number;
}

const OUTER_COLOR = 0x88eeff;
const INNER_COLOR = 0xffffff;

export function drawSelectionHalo(
  g: Phaser.GameObjects.Graphics,
  t: HaloTarget,
  style: HaloStyle = {},
): void {
  const gap = style.gap ?? 14;
  const inset = style.inset ?? 4;

  g.clear();

  const ow = t.w + gap * 2;
  const oh = t.h + gap * 2;
  const orx = t.rx + gap;
  const ory = t.ry + gap;
  const ox = t.cx - ow / 2;
  const oy = t.cy - oh / 2;

  g.lineStyle(3, OUTER_COLOR, 0.95);
  strokeRoundedRectXY(g, ox, oy, ow, oh, orx, ory);

  g.lineStyle(1, INNER_COLOR, 0.6);
  strokeRoundedRectXY(
    g,
    ox + inset,
    oy + inset,
    ow - inset * 2,
    oh - inset * 2,
    Math.max(orx - inset, 2),
    Math.max(ory - inset, 2),
  );
}

/**
 * Stroke a rounded rectangle whose corners can be elliptical (rx !== ry).
 * Phaser's built-in `strokeRoundedRect` only supports a single radius, so we
 * walk the four corner arcs as a point list and stroke it as one closed shape.
 */
export function strokeRoundedRectXY(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  rx: number,
  ry: number,
  segPerCorner = 10,
): void {
  rx = Math.min(rx, w / 2);
  ry = Math.min(ry, h / 2);

  const left = x + rx;
  const right = x + w - rx;
  const top = y + ry;
  const bottom = y + h - ry;

  const pts: Phaser.Types.Math.Vector2Like[] = [];
  const arc = (acx: number, acy: number, a0: number, a1: number) => {
    for (let i = 0; i <= segPerCorner; i++) {
      const a = a0 + (a1 - a0) * (i / segPerCorner);
      pts.push({ x: acx + rx * Math.cos(a), y: acy + ry * Math.sin(a) });
    }
  };

  arc(right, top, -Math.PI / 2, 0); // top-right
  arc(right, bottom, 0, Math.PI / 2); // bottom-right
  arc(left, bottom, Math.PI / 2, Math.PI); // bottom-left
  arc(left, top, Math.PI, Math.PI * 1.5); // top-left

  g.strokePoints(pts as Phaser.Math.Vector2[], true);
}
